/**
 * Consultas e gravações da tela de usuários pelo TanStack Query.
 *
 * Papel: `useUsuarios` mantém em cache as páginas da listagem e
 * `useSalvarPermissoes` grava uma grade e manda a listagem se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_USUARIOS`. A gravação
 * invalida esse prefixo inteiro, e não só a página aberta, porque a mesma pessoa
 * pode aparecer em outra página ou noutra busca já guardada no cache; uma chave
 * que não começasse por ele continuaria mostrando a grade antiga depois de
 * salvar.
 */
import { type DefinirPermissoes, type ParametrosListagem } from '@concierge/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { buscarUsuarios, salvarPermissoes } from '../api/usuarios';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_USUARIOS = ['usuarios'] as const;

/**
 * Lê uma página da listagem de usuários.
 *
 * A página anterior continua na tela enquanto a seguinte carrega, pelo
 * `keepPreviousData`. Sem isso, a tabela piscaria vazia a cada troca de página
 * ou a cada letra digitada na busca.
 *
 * @param parametros - Página, tamanho e busca; cada combinação ocupa uma entrada
 *   própria no cache.
 * @returns Resultado da consulta; `isPlaceholderData` verdadeiro indica que o que
 *   está na tela ainda é a página anterior.
 */
export function useUsuarios(parametros: ParametrosListagem) {
  return useQuery({
    queryKey: [...CHAVE_USUARIOS, 'lista', parametros],
    queryFn: ({ signal }) => buscarUsuarios(parametros, signal),
    placeholderData: keepPreviousData,
  });
}

/** Variáveis de `useSalvarPermissoes`. */
interface SalvarPermissoesVariaveis {
  id: string;
  dados: DefinirPermissoes;
}

/**
 * Grava a grade de permissões de uma pessoa.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa, e o formulário
 * precisa tratar essa rejeição para mostrar a mensagem; o sucesso já invalida a
 * listagem por conta própria.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarPermissoes() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarPermissoesVariaveis) => salvarPermissoes(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_USUARIOS }),
  });
}
